import { GetGroupRowAggParams } from 'ag-grid-community';
import { isNumber } from 'lodash';
import { MarkupDetailsBaseRow, MarkupDetailsRowTuple } from 'src/domain';
import { sumAverage, weightedAverage } from './average-total.agg';

export interface MarkupDetailsAggregation
    extends Omit<MarkupDetailsBaseRow, 'id' | 'category' | 'expectedGrossMargin' | 'expectedCogs'> {
    expectedGrossMargin: [number, number | undefined];
    expectedCogs: [number, number | undefined];
    grossProfitLY: number;
    grossSalesILY: number;
    grossSalesILLY: number;
    cogsLLY: number;
}

export const weightedAverageMarkupDetails = (params: GetGroupRowAggParams<MarkupDetailsRowTuple>) => {
    const items = params.nodes.map((n) => n.data).filter(Boolean) as MarkupDetailsRowTuple[];

    const weightedAvgs = weightedAverage(
        items,
        ['plannedValue', 'expectedGrossMargin'],
        (item) => item.plannedSplit / 100
    );

    const summedAvgs = sumAverage(items, ['cogsLY', 'cogsLLY', 'grossSalesILY', 'grossSalesILLY', 'expectedCogs']);

    const baseResult: MarkupDetailsAggregation = {
        valueLY: 0,
        valueLLY: 0,
        plannedValue: weightedAvgs.plannedValue ?? 0,
        expectedGrossMargin: Array.isArray(weightedAvgs.expectedGrossMargin)
            ? weightedAvgs.expectedGrossMargin
            : [weightedAvgs.expectedGrossMargin ?? 0, undefined],
        grossMarginLY: 0,
        grossProfitLY: 0,
        netSalesLY: 0,
        expectedCogs:
            Array.isArray(summedAvgs.expectedCogs) && summedAvgs.expectedCogs[0] !== summedAvgs.expectedCogs[1]
                ? [summedAvgs.expectedCogs[0], summedAvgs.expectedCogs[1]]
                : [summedAvgs.expectedCogs?.[0] ?? 0, undefined],
        cogsLY: summedAvgs.cogsLY ?? 0,
        cogsLLY: summedAvgs.cogsLLY ?? 0,
        grossSalesILY: summedAvgs.grossSalesILY ?? 0,
        grossSalesILLY: summedAvgs.grossSalesILLY ?? 0,
        plannedSplit: 0,
    };
    const result = params.nodes.reduce<MarkupDetailsAggregation>(
        (acc, value) => {
            const data = value.data;

            if (!data) return acc;

            const cogsLY = data?.cogsLY;
            const grossSalesILY = data?.grossSalesILY;
            const vat = data.vat;

            acc.plannedSplit = data.plannedSplit;

            if (isNumber(grossSalesILY) && isNumber(vat)) {
                acc.grossProfitLY += grossSalesILY / vat - cogsLY;
            }

            if (data.valueLY !== -999 && acc.grossSalesILY !== 0 && acc.cogsLY !== 0)
                acc.valueLY = acc.grossSalesILY / acc.cogsLY;
            if (acc.grossSalesILLY !== 0 && acc.cogsLLY !== 0) acc.valueLLY = acc.grossSalesILLY / acc.cogsLLY;
            if (isNumber(vat)) acc.grossMarginLY = (acc.grossProfitLY / (acc.grossSalesILY / vat)) * 100;

            return acc;
        },
        {
            ...baseResult,
        }
    );

    return result;
};
